import React, { useState, useEffect, useContext } from "react";
import { useParams } from "react-router-dom";
import InfiniteScroll from "react-infinite-scroll-component";
import ImageCard from "./ImageCard";
import SkeletonCard from "./SkeletonCard";
import DataContext from "../context/DataContext";

// `category` is undefined for the "Home" (all posts) view, or a string like
// "AI" / "Security" / "Gadgets" when arriving from the nav bar.
function ImageList({ category }) {
  const { items, setItems } = useContext(DataContext);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  // Tracks whether a request is currently in flight. This is the key fix:
  // react-infinite-scroll-component calls `next` on its own as soon as the
  // (still-empty) list is shorter than the viewport — i.e. immediately on
  // mount, before page 1 has even come back. Without this guard, that
  // eager call bumps `page` to 2 regardless of what total_pages turns out
  // to be, producing an extra request every single time.
  const [loading, setLoading] = useState(true);

  // Clear whatever was loaded for the previous view. This effect only runs
  // once per mount, and the component is force-remounted on category change
  // (see the `key` prop in App.js), so this reliably fires exactly once
  // per category switch, before the fetch effect below.
  useEffect(() => {
    setItems([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    let ignore = false;

    const fetchData = async (pageNumber) => {
      setLoading(true);
      try {
        const url = category
          ? `https://api.tekomania.com/getData?page=${pageNumber}&category=${encodeURIComponent(category)}`
          : `https://api.tekomania.com/getData?page=${pageNumber}`;
        const response = await fetch(url, { signal: controller.signal });
        const data = await response.json();

        // If this effect was cleaned up (StrictMode's double-invoke in dev,
        // a fast category switch, or an unmount) before the response came
        // back, discard it — don't let a stale response overwrite newer
        // state.
        if (ignore) return;

        // Guard: ensure data is an array
        if (!Array.isArray(data.items) || data.items.length === 0) {
          setHasMore(false);
          return;
        }

        // Deduplicate by id
        setItems((prev) => {
          const map = new Map();
          [...prev, ...data.items].forEach(item => map.set(item.id, item));
          return Array.from(map.values());
        });

        // Only page 2+ if the server actually says there's more.
        setHasMore(pageNumber < data.total_pages);
      } catch (error) {
        if (error.name !== "AbortError") {
          console.error("Error fetching data:", error);
          setHasMore(false);
        }
      } finally {
        if (!ignore) setLoading(false);
      }
    };

    fetchData(page);

    return () => {
      ignore = true;
      controller.abort();
    };
  }, [page, category, setItems]);

  const fetchMoreData = () => {
    if (loading || !hasMore) return;
    setPage((prev) => prev + 1);
  };

  return (
    <InfiniteScroll
      dataLength={items.length}
      next={fetchMoreData}
      hasMore={hasMore}
      loader={
        <div className="image-grid skeleton-grid">
          {Array.from({ length: 3 }).map((_, i) => (
            <SkeletonCard key={i} />
          ))}
        </div>
      }
      endMessage={
        items.length > 0 ? (
          <p className="end-message">You're all caught up 🎉</p>
        ) : (
          <p className="end-message">No posts in this category yet.</p>
        )
      }
    >
      <div className="image-grid">
        {items.map((item) => (
          <ImageCard key={item.id} item={item} />
        ))}
      </div>
    </InfiniteScroll>
  );
}

// Thin wrapper so /category/:category can pull the param and remount
// ImageList (via `key`) whenever the category changes.
export function ImageListRoute() {
  const { category } = useParams();
  return <ImageList key={category} category={category} />;
}

export default ImageList;